<template lang="pug">
v-row#suus__event-form__description.mb-8
    v-col(cols="12")
        Subtitle(
          text="Beschreibung",
          :fontSize="1.2",
          :fontWeight="900"
        )
        ForumWysiwygWithUploader(
          v-model="description"
          :getMentionables="getMentionableFunc"
          :load-attachment="loadAttachment"
          @attachments-valid="updateAttachmentsValid",
        )
</template>

<script lang="ts">
import TextBody from "../../../../components/UI/TextBody.vue"
import {
  computed,
  defineComponent,
  inject,
  PropType,
  ref,
  WritableComputedRef,
} from "vue"
import useMentionSearch from "concerns/useMentionSearch"
import EventFormService from "features/events/form/event-form.service"
import { EventForm } from "components/EventForm/EventForm.vue"
import Subtitle from "components/UI/Subtitle.vue"
import useTranslate from "features/common/translate/concerns/useTranslate"
import { FileAndId } from "app/modules/core/types/dtos"
import { Mentionable } from "components/Wysiwyg/extensions/suus-mention/SuusMention"
import BasicWysiwyg from "components/Wysiwyg/BasicWysiwyg.vue"
import { Rule } from "features/common/forms/form.service"
import { PanelIdentifier } from "features/events/form/event-form.types"
import ForumWysiwygWithUploader from "components/Wysiwyg/ForumWysiwygWithUploader.vue"
import { eventsApi } from "store/api"
import { Attachment } from "suus-api"

const DescriptionFields = defineComponent({
  name: "DescriptionFields",
  components: { ForumWysiwygWithUploader, BasicWysiwyg, Subtitle, TextBody },
  props: {
    onFileDrop: {
      type: Function as PropType<(file: FileAndId) => void>,
      required: false,
    },
  },
  methods: {
    async getMentionableFunc(query): Promise<Mentionable[]> {
      const { mentionableFunction } = useMentionSearch(this.viewContext)
      return await mentionableFunction(query)
    },
  },
  setup(props) {
    const eventFormService: EventFormService<EventForm> =
      inject("EventFormService")
    const { t$ } = useTranslate("components.event_form")
    eventFormService.registerProperty({
      name: "description",
    })

    const attachmentsValid = ref(true)
    const updateAttachmentsValid = (valid: boolean) => {
      attachmentsValid.value = valid
    }
    const rule: Rule<EventForm, "description"> = {
      field: "description",
      selector: "event-form__description",
      message: t$("components.event_form.validate_attachments"),
      panelIdentifier: PanelIdentifier.GENERAL,
      action: (v) => attachmentsValid.value,
    }

    eventFormService.registerRule(rule as Rule<EventForm>)

    const description: WritableComputedRef<string> = computed({
      get(): string {
        return eventFormService.getFormValue("description").value || ""
      },
      set(value: string): void {
        eventFormService.setFormValue("description", value)
      },
    })

    const viewContext = computed(() => {
      const group = eventFormService.getFormValue("group").value
      return {
        group: group,
        level_id: group?.level_id,
        client_id: group?.client_id,
      }
    })

    const attachments = ref<Attachment[]>([])

    const loadAttachments = async (): Promise<Attachment[]> => {
      const eventId = eventFormService.currentId.toString()

      // if new event is created, return empty array
      if (!eventId) {
        return []
      }

      const { data } = await eventsApi.getEventAttachments(eventId)
      return data
    }

    // Delay loading until there is the first request for it & do it only once
    const getAttachments = async () => {
      if (attachments.value.length === 0) {
        attachments.value = await loadAttachments()
        console.log("Loading attachments the first time", attachments.value)
      }
      console.log("Returning already loaded attachments", attachments.value)
      return attachments.value
    }

    const loadAttachment = async (frontendId: string): Promise<Attachment> => {
      const attachments = await getAttachments()
      const attachment = attachments.find((a) => a.frontend_id === frontendId)
      if (attachment) {
        return attachment
      } else {
        console.error("Attachment not found!", frontendId, attachments)
        throw "Attachment not found!"
      }
    }

    return {
      description,
      viewContext,
      loadAttachment,
      updateAttachmentsValid,
    }
  },
})

export default DescriptionFields
</script>
